const MIME_BY_EXTENSION: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    txt: 'text/plain',
    csv: 'text/csv',
    json: 'application/json',
    html: 'text/html',
    htm: 'text/html',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    doc: 'application/msword',
};

const AUTO_DOWNLOAD_EXTENSIONS = new Set(['doc', 'docx', 'xls', 'xlsx']);

/** Blob types that carry no useful preview hint — prefer the filename extension instead. */
const GENERIC_BLOB_TYPES = new Set([
    'application/octet-stream',
    'binary/octet-stream',
    'application/force-download',
    'application/download',
    'application/x-download',
]);

function normalizeMimeType(value?: string): string | undefined {
    if (!value) return undefined;
    const normalized = value.split(';')[0].trim().toLowerCase();
    return normalized || undefined;
}

function isGenericBlobType(mimeType?: string): boolean {
    if (!mimeType) return true;
    return GENERIC_BLOB_TYPES.has(mimeType);
}

export function getFileExtension(filename: string): string | undefined {
    const baseName = filename.trim().split(/[/\\]/).pop() || filename.trim();
    const lastDot = baseName.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === baseName.length - 1) return undefined;
    return baseName.slice(lastDot + 1).toLowerCase();
}

export function isZipFilename(filename: string): boolean {
    const extension = getFileExtension(filename);
    return extension === 'zip';
}

export function isOfficeDocumentFilename(filename: string): boolean {
    const extension = getFileExtension(filename);
    return extension ? AUTO_DOWNLOAD_EXTENSIONS.has(extension) : false;
}

export function isPdfFile(filename: string, mimeType?: string): boolean {
    return guessMimeType(filename, mimeType) === 'application/pdf';
}

type PdfPreviewHandler = (blob: Blob, filename: string, mimeType?: string) => void;

let pdfPreviewHandler: PdfPreviewHandler | null = null;

/** Register the in-app embedpdf viewer (set by PdfViewerProvider). */
export function setPdfPreviewHandler(handler: PdfPreviewHandler | null): void {
    pdfPreviewHandler = handler;
}

/** Build attachment filename for an edited main PDF: `{stem}_COMMENTED.pdf` */
export function buildCommentedPdfFilename(
    originalFilename: string,
    existingNames?: Set<string>
): string {
    let stem = originalFilename.toLowerCase().endsWith('.pdf')
        ? originalFilename.slice(0, -4)
        : originalFilename;
    stem = stem.replace(/^marked-up-/, '').replace(/_COMMENTED(-[^.]+)?$/i, '');
    const baseName = `${stem}_COMMENTED.pdf`;
    if (!existingNames || !existingNames.has(baseName)) {
        return baseName;
    }
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    return `${stem}_COMMENTED-${timestamp}.pdf`;
}

export function guessMimeType(filename: string, blobType?: string): string {
    const extension = getFileExtension(filename);
    const extensionMime = extension ? MIME_BY_EXTENSION[extension] : undefined;
    const normalizedBlobType = normalizeMimeType(blobType);

    if (extensionMime && isGenericBlobType(normalizedBlobType)) {
        return extensionMime;
    }

    return normalizedBlobType || extensionMime || 'application/octet-stream';
}

export function parseContentDispositionFilename(
    contentDisposition: string | null | undefined,
    fallback: string
): string {
    if (!contentDisposition) return fallback;
    const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (matches?.[1]) {
        return matches[1].replace(/['"]/g, '');
    }
    return fallback;
}

export async function resolveStorageEnvelopeBlob(
    blob: Blob,
    contentType = ''
): Promise<Blob> {
    const normalizedContentType = contentType.toLowerCase();
    const isLikelyJsonEnvelope =
        normalizedContentType.includes('application/json') ||
        blob.type.includes('application/json') ||
        (blob.size > 0 && blob.size <= 1024 * 1024);

    if (!isLikelyJsonEnvelope) {
        return blob;
    }

    try {
        const text = (await blob.text()).trim();
        if (!text || (!text.startsWith('{') && !text.startsWith('['))) {
            return blob;
        }

        const data = JSON.parse(text) as { download_url?: string };
        if (!data.download_url) {
            return blob;
        }

        const externalResponse = await fetch(data.download_url);
        if (!externalResponse.ok) {
            throw new Error('Failed to fetch file from storage');
        }
        return await externalResponse.blob();
    } catch (error) {
        throw error instanceof Error
            ? error
            : new Error('Failed to resolve storage download URL');
    }
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function getViewerElement(mimeType: string, fileUrl: string): string {
    if (mimeType === 'application/pdf') {
        return `<iframe src="${fileUrl}" title="Preview"></iframe>`;
    }
    if (mimeType.startsWith('image/')) {
        return `<img src="${fileUrl}" alt="Preview" />`;
    }
    if (mimeType.startsWith('text/') || mimeType === 'application/json') {
        return `<iframe src="${fileUrl}" title="Preview"></iframe>`;
    }
    return `<p class="fallback">Preview is not available for this file type. Use Download to save the file.</p>`;
}

/** Trigger a direct browser download (used for ZIP archives). */
export function triggerBlobDownload(blob: Blob, filename: string, mimeType?: string): void {
    const type = guessMimeType(filename, mimeType || blob.type);
    const fileBlob = blob.type === type ? blob : new Blob([blob], { type });
    const url = window.URL.createObjectURL(fileBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

/**
 * Open a file preview in a new tab. The tab title and download action use the original filename.
 * File bytes stay in memory via blob URLs — S3 links are never exposed to the user.
 */
export function openFilePreviewInNewTab(blob: Blob, filename: string, mimeType?: string): Window | null {
    const type = guessMimeType(filename, mimeType || blob.type);
    const fileBlob = blob.type === type ? blob : new Blob([blob], { type });

    if (type === 'application/pdf' && pdfPreviewHandler) {
        pdfPreviewHandler(fileBlob, filename, type);
        return null;
    }

    const win = window.open('about:blank', '_blank');
    if (!win) {
        throw new Error('Popup blocked. Please allow popups to preview files.');
    }

    const fileUrl = URL.createObjectURL(fileBlob);
    const escapedFilename = escapeHtml(filename);
    const viewer = getViewerElement(type, fileUrl);

    win.document.open();
    win.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapedFilename}</title>
  <style>
    html, body { margin: 0; height: 100%; background: #1a1a1a; }
    body { display: flex; flex-direction: column; font-family: system-ui, -apple-system, sans-serif; }
    header {
      display: flex; align-items: center; gap: 12px; padding: 8px 16px;
      background: #0f2134; color: #ccba9b; flex-shrink: 0;
    }
    header .name {
      flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600;
    }
    header a {
      color: #0f2134; background: #ccba9b; text-decoration: none; font-weight: 600;
      padding: 6px 12px; border-radius: 4px; font-size: 0.85rem; flex-shrink: 0;
    }
    header a:hover { opacity: 0.9; }
    .viewer { flex: 1; min-height: 0; background: #2a2a2a; display: flex; align-items: center; justify-content: center; }
    .viewer iframe, .viewer object, .viewer embed { width: 100%; height: 100%; border: 0; display: block; }
    .viewer img { max-width: 100%; max-height: 100%; object-fit: contain; }
    .fallback { padding: 16px; color: #f5f5f5; font-family: system-ui, sans-serif; }
  </style>
</head>
<body>
  <header>
    <span class="name">${escapedFilename}</span>
    <a href="${fileUrl}" download="${escapedFilename}">Download</a>
  </header>
  <div class="viewer">${viewer}</div>
  <script>
    window.addEventListener('beforeunload', function() {
      URL.revokeObjectURL('${fileUrl}');
    });
  </script>
</body>
</html>`);
    win.document.close();

    return win;
}

/** Preview in a new tab, or download directly for ZIP and Office documents. */
export function openFilePreviewOrDownload(blob: Blob, filename: string, mimeType?: string): void {
    if (isZipFilename(filename) || isOfficeDocumentFilename(filename)) {
        triggerBlobDownload(blob, filename, mimeType);
        return;
    }
    openFilePreviewInNewTab(blob, filename, mimeType);
}
